/**
 * OKX WebSocket API
 * @see https://www.okx.com/docs-v5/en/#websocket-api-public-channel
 */

import ReconnectingWebSocket from 'reconnecting-websocket';

import type {
  OkxWsDepthMessage,
  OkxWsKlineMessage,
  OkxWsSubscribeRequest,
  OkxWsSubscribeResponse,
  OkxWsTickerMessage,
  OkxWsTradeMessage,
} from './types';

import type { WsChannelDescriptor, WsTopologyInfo } from '../../lib/types';

export const OKX_WS_PUBLIC_URL = 'wss://ws.okx.com:8443/ws/v5/public';
export const OKX_WS_BUSINESS_URL = 'wss://ws.okx.com:8443/ws/v5/business';
export const OKX_WS_PRIVATE_URL = 'wss://wspri.okx.com:8443/ws/v5/ipublic';

/**
 * OKX WebSocket channel descriptors.
 */
export const OKX_WS_CHANNELS: Record<string, WsChannelDescriptor> = {
  ticker: {
    channel: 'tickers',
    topic: 'tickers',
    supportsBatching: true,
    docUrl: 'https://www.okx.com/docs-v5/en/#order-book-trading-market-data-ws-tickers-channel',
  },
  depth: {
    channel: 'books',
    topic: 'books',
    supportsBatching: true,
    docUrl: 'https://www.okx.com/docs-v5/en/#order-book-trading-market-data-ws-order-book-channel',
  },
  trades: {
    channel: 'trades',
    topic: 'trades',
    supportsBatching: true,
    docUrl: 'https://www.okx.com/docs-v5/en/#order-book-trading-market-data-ws-trades-channel',
  },
  klines: {
    channel: 'candle',
    topic: 'candle<interval>',
    supportsBatching: true,
    docUrl:
      'https://www.okx.com/docs-v5/en/#order-book-trading-market-data-ws-candlesticks-channel',
  },
};

/**
 * OKX WebSocket topology information.
 * CRITICAL: Multiple endpoints available (public, business, ipublic).
 * Business endpoint used for klines, public for ticker/trades/depth.
 * Server expects ping every 30 seconds.
 */
export const OKX_WS_TOPOLOGY: WsTopologyInfo = {
  baseUrl: OKX_WS_PUBLIC_URL,
  singleConnectionPerChannel: false,
  heartbeatIntervalMs: 29000, // Send ping every 29 seconds (server timeout is 30s)
  note: 'Multiple endpoints: public (ticker/trades/depth), business (candles), ipublic (private orderbook). Supports batched subscriptions. Orderbook uses incremental updates with action field.',
};

// ========================================
// WebSocket Subscription Options
// ========================================

export type OkxWsStatus = 'connecting' | 'connected' | 'reconnecting' | 'disconnected' | 'error';

export interface OkxWsSubscriptionOptions<T> {
  /** OKX WebSocket channel (e.g., "tickers", "books", "trades", "candle1m") */
  channel: string;
  /** Instrument ID (e.g., "BTC-USDT") */
  instId: string;
  /** Callback for successfully decoded messages */
  onMessage: (data: T) => void;
  /** Callback for errors (connection, subscription failures) */
  onError?: (error: Error) => void;
  /** Callback for connection status changes */
  onStatusChange?: (status: OkxWsStatus) => void;
  /** Base WebSocket URL (defaults to OKX_WS_PUBLIC_URL) */
  baseUrl?: string;
  /** Custom WebSocket configuration */
  wsConfig?: {
    maxRetries?: number;
    minReconnectionDelay?: number;
  };
}

// ========================================
// Generic WebSocket Subscription
// ========================================

/**
 * Create a generic OKX WebSocket subscription.
 * Handles ping/pong, subscription management, and automatic reconnection.
 *
 * @returns ReconnectingWebSocket instance (call .close() to unsubscribe)
 */
function createOkxWsSubscription<T>(options: OkxWsSubscriptionOptions<T>): ReconnectingWebSocket {
  const {
    channel,
    instId,
    onMessage,
    onError,
    onStatusChange,
    baseUrl = OKX_WS_PUBLIC_URL,
    wsConfig = {},
  } = options;

  const config = {
    maxRetries: wsConfig.maxRetries ?? 10,
    minReconnectionDelay: wsConfig.minReconnectionDelay ?? 1000,
  };

  let hasConnectedOnce = false;
  let pingInterval: ReturnType<typeof setInterval> | undefined;

  onStatusChange?.('connecting');

  const ws = new ReconnectingWebSocket(baseUrl, [], config);

  ws.addEventListener('open', () => {
    hasConnectedOnce = true;
    onStatusChange?.('connected');

    // Send subscription request
    const subscribeMsg: OkxWsSubscribeRequest = {
      op: 'subscribe',
      args: [
        {
          channel,
          instId,
        },
      ],
    };

    ws.send(JSON.stringify(subscribeMsg));

    // Start ping interval (send "ping" every 29 seconds)
    if (pingInterval) {
      clearInterval(pingInterval);
    }
    pingInterval = setInterval(() => {
      if (ws.readyState === WebSocket.OPEN) {
        ws.send('ping');
      }
    }, 29000);
  });

  ws.addEventListener('message', (event: MessageEvent) => {
    try {
      // OKX sends "pong" in response to "ping"
      if (event.data === 'pong') {
        return;
      }

      const payload = JSON.parse(event.data);

      // Handle subscription response
      const subResponse = payload as OkxWsSubscribeResponse;
      if (subResponse.event === 'subscribe') {
        // Subscription successful
        return;
      }

      if (subResponse.event === 'error') {
        onError?.(
          new Error(`OKX subscription error: code=${subResponse.code}, msg=${subResponse.msg}`),
        );
        return;
      }

      // Forward data messages to callback
      onMessage(payload as T);
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      onError?.(err);
      onStatusChange?.('error');
    }
  });

  ws.addEventListener('close', () => {
    if (pingInterval) {
      clearInterval(pingInterval);
    }
    onStatusChange?.(hasConnectedOnce ? 'reconnecting' : 'disconnected');
  });

  ws.addEventListener('error', event => {
    const error = new Error(`OKX WebSocket error: ${event.type}`);
    onError?.(error);
    onStatusChange?.('error');
  });

  return ws;
}

// ========================================
// Ticker Subscription
// ========================================

export interface OkxTickerSubscriptionOptions {
  /** Instrument ID (e.g., "BTC-USDT") */
  instId: string;
  /** Callback for ticker updates */
  onMessage: (ticker: OkxWsTickerMessage) => void;
  /** Callback for errors */
  onError?: (error: Error) => void;
  /** Callback for connection status changes */
  onStatusChange?: (status: OkxWsStatus) => void;
}

/**
 * Subscribe to OKX ticker updates via WebSocket.
 * @see https://www.okx.com/docs-v5/en/#order-book-trading-market-data-ws-tickers-channel
 */
export function subscribeOkxTicker(options: OkxTickerSubscriptionOptions): ReconnectingWebSocket {
  const { instId, onMessage, onError, onStatusChange } = options;

  return createOkxWsSubscription<OkxWsTickerMessage>({
    channel: 'tickers',
    instId,
    onMessage,
    onError,
    onStatusChange,
    baseUrl: OKX_WS_PUBLIC_URL,
  });
}

// ========================================
// Orderbook Depth Subscription
// ========================================

export interface OkxDepthSubscriptionOptions {
  /** Instrument ID (e.g., "BTC-USDT") */
  instId: string;
  /**
   * Channel type: "books" (400 levels), "books5" (5 levels),
   * "books-l2-tbt" (tick-by-tick), "books50-l2-tbt" (50 levels)
   */
  channel?: 'books' | 'books5' | 'books-l2-tbt' | 'books50-l2-tbt';
  /** Callback for orderbook updates */
  onMessage: (depth: OkxWsDepthMessage) => void;
  /** Callback for errors */
  onError?: (error: Error) => void;
  /** Callback for connection status changes */
  onStatusChange?: (status: OkxWsStatus) => void;
}

/**
 * Subscribe to OKX orderbook depth updates via WebSocket.
 * IMPORTANT: OKX sends both snapshot (action: "snapshot") and
 * incremental updates (action: "update").
 *
 * @see https://www.okx.com/docs-v5/en/#order-book-trading-market-data-ws-order-book-channel
 */
export function subscribeOkxDepth(options: OkxDepthSubscriptionOptions): ReconnectingWebSocket {
  const { instId, channel = 'books', onMessage, onError, onStatusChange } = options;

  return createOkxWsSubscription<OkxWsDepthMessage>({
    channel,
    instId,
    onMessage,
    onError,
    onStatusChange,
    baseUrl: OKX_WS_PUBLIC_URL,
  });
}

// ========================================
// Trades Subscription
// ========================================

export interface OkxTradeSubscriptionOptions {
  /** Instrument ID (e.g., "BTC-USDT") */
  instId: string;
  /** Callback for trade updates */
  onMessage: (trade: OkxWsTradeMessage) => void;
  /** Callback for errors */
  onError?: (error: Error) => void;
  /** Callback for connection status changes */
  onStatusChange?: (status: OkxWsStatus) => void;
}

/**
 * Subscribe to OKX public trade updates via WebSocket.
 * @see https://www.okx.com/docs-v5/en/#order-book-trading-market-data-ws-trades-channel
 */
export function subscribeOkxTrades(options: OkxTradeSubscriptionOptions): ReconnectingWebSocket {
  const { instId, onMessage, onError, onStatusChange } = options;

  return createOkxWsSubscription<OkxWsTradeMessage>({
    channel: 'trades',
    instId,
    onMessage,
    onError,
    onStatusChange,
    baseUrl: OKX_WS_PUBLIC_URL,
  });
}

// ========================================
// Kline (Candlestick) Subscription
// ========================================

export interface OkxKlineSubscriptionOptions {
  /** Instrument ID (e.g., "BTC-USDT") */
  instId: string;
  /** Interval: "1m", "3m", "5m", "15m", "30m", "1H", "2H", "4H", "6H", "12H", "1D", "1W", "1M", "3M", "6M", "1Y" */
  interval: string;
  /** Callback for kline updates */
  onMessage: (kline: OkxWsKlineMessage) => void;
  /** Callback for errors */
  onError?: (error: Error) => void;
  /** Callback for connection status changes */
  onStatusChange?: (status: OkxWsStatus) => void;
}

/**
 * Subscribe to OKX kline (candlestick) updates via WebSocket.
 * IMPORTANT: Uses business WebSocket endpoint (wss://ws.okx.com:8443/ws/v5/business).
 * IMPORTANT: Timestamps in data are in MILLISECONDS.
 * IMPORTANT: Data array format: [ts, o, h, l, c, vol, volCcy, volCcyQuote, confirm]
 *
 * @see https://www.okx.com/docs-v5/en/#order-book-trading-market-data-ws-candlesticks-channel
 */
export function subscribeOkxKlines(options: OkxKlineSubscriptionOptions): ReconnectingWebSocket {
  const { instId, interval, onMessage, onError, onStatusChange } = options;

  // Map interval to OKX channel format (e.g., "1m" -> "candle1m")
  const channel = `candle${interval}`;

  return createOkxWsSubscription<OkxWsKlineMessage>({
    channel,
    instId,
    onMessage,
    onError,
    onStatusChange,
    baseUrl: OKX_WS_BUSINESS_URL, // Use business endpoint for klines
  });
}

// ========================================
// Interval Helpers
// ========================================

/**
 * Parse OKX interval string to milliseconds for calculating closeTime.
 *
 * IMPORTANT: OKX uses case-sensitive interval units:
 * - lowercase 'm' = minutes (e.g., '1m', '5m', '15m')
 * - uppercase 'M' = months (e.g., '1M', '3M', '6M')
 * - uppercase 'H' = hours (e.g., '1H', '4H')
 * - uppercase 'D' = days
 * - uppercase 'W' = weeks
 * - uppercase 'Y' = years
 *
 * Do NOT use toUpperCase() as it conflates minutes and months!
 */
export function parseOkxIntervalToMs(interval: string): number {
  const match = interval.match(/^(\d+)([a-zA-Z]+)$/);
  if (!match) {
    return 60_000; // default to 1 minute
  }

  const [, valueStr, unit] = match;
  const value = Number(valueStr);

  // IMPORTANT: Case-sensitive matching - 'm' is minutes, 'M' is months
  switch (unit) {
    case 'm':
      return value * 60_000; // minutes
    case 'H':
      return value * 3_600_000; // hours
    case 'D':
      return value * 86_400_000; // days
    case 'W':
      return value * 604_800_000; // weeks
    case 'M':
      return value * 2_592_000_000; // months (30 days)
    case 'Y':
      return value * 31_536_000_000; // years (365 days)
    default:
      return 60_000;
  }
}
